import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { applyDocumentMeta } from "./documentMeta";
import { LOCALES, messages } from "./locales";

const STORAGE_KEY = "nfw-lang";
const I18nContext = createContext(null);

export function detectLocale() {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && messages[saved]) return saved;
  } catch (err) {
    /* ignore */
  }
  const nav = (window.navigator.language || "en").toLowerCase();
  if (nav.startsWith("zh")) {
    if (
      nav.includes("tw") ||
      nav.includes("hk") ||
      nav.includes("mo") ||
      nav.includes("hant")
    ) {
      return "zh-TW";
    }
    return "zh-CN";
  }
  return "en";
}

export function AccentText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\{\{[\s\S]*?\}\})/g);
  return parts.map((part, i) => {
    const match = part.match(/^\{\{([\s\S]*)\}\}$/);
    if (match) return <span key={i}>{match[1]}</span>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(detectLocale);

  const setLocale = useCallback((next) => {
    if (!messages[next]) return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      /* ignore */
    }
  }, []);

  const t = messages[locale] || messages.en;

  useLayoutEffect(() => {
    applyDocumentMeta({ locale });
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t, locales: LOCALES }),
    [locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useDocumentMeta({ title, description, path }) {
  const { locale } = useI18n();
  useLayoutEffect(() => {
    applyDocumentMeta({ title, description, path, locale });
  }, [title, description, path, locale]);
}

export { applyDocumentMeta } from "./documentMeta";
